import * as Sentry from "@sentry/react-native";
import config from "@/config";

/**
 * Sentry client integration for the Expo app (web + native).
 *
 * Privacy boundary (see beta-observability.md §7a). `applyPrivacyBoundary`
 * runs in `beforeSend` on every event and:
 *   - Recursively walks `event.extra`, `event.tags`, `event.contexts`,
 *     `event.breadcrumbs[*].data`, and `event.exception.values[*].mechanism.data`,
 *     deleting any key matching the PII regex at any depth. Cycle-safe and
 *     depth-capped so a malformed payload can't OOM the SDK.
 *   - Redacts email-shaped substrings from string-valued fields the recursive
 *     pass can't touch: `event.message`, `event.exception.values[*].value`,
 *     and `event.breadcrumbs[*].message`. Firebase auth errors put the user's
 *     email in the exception value for codes like
 *     `auth/account-exists-with-different-credential`.
 *   - Strips query strings from breadcrumb URLs (Firebase tokens leak there).
 *   - Removes `user.email` / `user.username` / `user.ip_address`. Only
 *     `user.id = firebase_uid` is allowed through.
 *
 * Quota defense (see beta-observability.md §7a):
 *   - `dedupeIntegration` — Sentry's built-in identical-fingerprint drop.
 *   - In-memory per-fingerprint token bucket — caps a `useEffect` render-loop
 *     throw at MAX_EVENTS_PER_WINDOW within WINDOW_MS so it can't burn the
 *     free-tier quota in minutes.
 *   - Conservative `maxBreadcrumbs`.
 *   - Sentry project-level inbound rate limit is the server-side backstop.
 */

const { dsn, environment } = config.sentry;
const { release } = config;

// Keys to delete outright when seen at any nesting depth. `message` is in the
// list because Firebase / fetch error objects often carry user content there;
// the readable error text we still want survives via the redacted `value` on
// `event.exception.values[*]` (see redactEmails below).
const PII_KEY_RE = /email|password|conversation|transcript|message/i;

// Conservative email pattern — RFC-correct enough for redaction. We don't try
// to redact other PII shapes (phone, SSN, etc.) because the precision cost is
// too high for the scope. Add to this list as real leaks surface.
const EMAIL_RE = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g;

// Bound recursion. Sentry's default `normalizeDepth` is 3; we go a little
// deeper to cover its own wrapping. Cycle-safe via WeakSet of seen objects.
const MAX_SCRUB_DEPTH = 6;

const WINDOW_MS = 60_000;
const MAX_EVENTS_PER_WINDOW = 5;
const fingerprintCounts = new Map<
  string,
  { count: number; firstSeen: number }
>();

function fingerprintFor(event: Sentry.ErrorEvent): string {
  const exc = event.exception?.values?.[0];
  if (exc) return `${exc.type ?? ""}:${exc.value ?? ""}`;
  return event.message ?? "unknown";
}

function shouldDrop(fingerprint: string): boolean {
  const now = Date.now();
  const entry = fingerprintCounts.get(fingerprint);
  if (!entry || now - entry.firstSeen > WINDOW_MS) {
    fingerprintCounts.set(fingerprint, { count: 1, firstSeen: now });
    return false;
  }
  entry.count += 1;
  return entry.count > MAX_EVENTS_PER_WINDOW;
}

function redactEmails(s: string): string {
  return s.replace(EMAIL_RE, "[email]");
}

function scrubObject(
  value: unknown,
  depth = 0,
  seen: WeakSet<object> = new WeakSet()
): void {
  if (depth > MAX_SCRUB_DEPTH) return;
  if (value === null || typeof value !== "object") return;
  if (seen.has(value as object)) return;
  seen.add(value as object);

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      const item = value[i];
      if (typeof item === "string") {
        value[i] = redactEmails(item);
      } else {
        scrubObject(item, depth + 1, seen);
      }
    }
    return;
  }

  const obj = value as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    if (PII_KEY_RE.test(key)) {
      delete obj[key];
      continue;
    }
    const v = obj[key];
    if (typeof v === "string") {
      obj[key] = redactEmails(v);
    } else {
      scrubObject(v, depth + 1, seen);
    }
  }
}

function stripQueryString(url: string): string {
  const idx = url.indexOf("?");
  return idx === -1 ? url : url.slice(0, idx);
}

/**
 * Apply the privacy boundary to a Sentry event in place. Exported for tests
 * — `beforeSend` calls this directly.
 */
export function applyPrivacyBoundary(event: Sentry.ErrorEvent): void {
  scrubObject(event.extra);
  scrubObject(event.tags);
  scrubObject(event.contexts);

  if (typeof event.message === "string") {
    event.message = redactEmails(event.message);
  }

  if (event.exception?.values) {
    for (const v of event.exception.values) {
      if (typeof v.value === "string") {
        v.value = redactEmails(v.value);
      }
      scrubObject(v.mechanism?.data);
    }
  }

  if (event.breadcrumbs) {
    for (const bc of event.breadcrumbs) {
      if (bc.data && typeof bc.data.url === "string") {
        bc.data.url = stripQueryString(bc.data.url);
      }
      if (typeof bc.message === "string") {
        bc.message = redactEmails(bc.message);
      }
      scrubObject(bc.data);
    }
  }

  if (event.user) {
    delete event.user.email;
    delete event.user.username;
    delete event.user.ip_address;
  }
}

/**
 * Initialize Sentry. No-op if config.sentry.dsn is unset, so dev builds and
 * tests don't need a DSN.
 */
export function initSentry(): void {
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment,
    release,
    sampleRate: 1.0,
    tracesSampleRate: 0,
    maxBreadcrumbs: 30,
    integrations: [Sentry.dedupeIntegration()],
    beforeSend(event) {
      if (shouldDrop(fingerprintFor(event))) return null;
      applyPrivacyBoundary(event);
      return event;
    }
  });
}

/**
 * Tag subsequent events with the Firebase UID. Pass `null` on sign-out.
 */
export function setSentryUser(firebaseUid: string | null): void {
  if (!dsn) return;
  Sentry.setUser(firebaseUid ? { id: firebaseUid } : null);
}
